import { maxBy, uniq } from 'es-toolkit';
import React, { ReactNode, useCallback, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import {
  AnalysisSettings,
  ChordBracing,
  ChordBracingSettings,
  DesignStandard,
  FloorDeflectionSettings,
  getTpi1Year,
  isTpi1VersionOrLater,
  isTruss,
  RoofDeflectionSettings,
  tpiSection,
  TrussType,
} from '@paragon/contract-library';
import { distanceGreaterThan } from '@paragon/geometry-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  getComponentTypeForSettingsPanel,
  getSelectedModelForSettingsPanel,
  SettingsPanelSelectedModel,
} from '../../../../../store/selectors/settingsPanelSelectors';
import { getDeterminateValueOrNull } from '../../../../dialogs/project-wide-settings/LoadedProjectWideSettingsDialog';
import { useAppSelector } from '../../../../hooks';
import DeflectionLimitSelect from '../../elements/DeflectionLimitSelect';
import ChordBracingSubPanel from '../../properties/ChordBracingSubPanel';
import { SettingsControlsProps } from '../interfaces';
import { formatBuildingCodeOrDesignStandard } from '../loading/DesignCodeField';
import { MultiValueBooleanInput } from '../multi-value-controls/MultiValueBooleanInput';
import { MultiValueDistanceInput } from '../multi-value-controls/MultiValueDistanceInput';
import { MultiValueNumberInput } from '../multi-value-controls/MultiValueNumberInput';
import { SettingsControlsPresetToolbar } from '../preset-toolbar/SettingsControlsPresetToolbar';

export default React.memo(AnalysisSettingsControls);

export interface Props {
  readonly isViewOnly: boolean;

  readonly showHeader?: boolean /** default: true */;
  readonly showPresetControls?: boolean /** default: true */;

  /**
   * The selected model informs whether component-specific warnings are shown. This prop allows overriding that
   *   in cases where the selected model should not be relied upon.
   */
  readonly selectedModelOverride?: SettingsPanelSelectedModel | null;

  readonly analysisSettingsArray: readonly AnalysisSettings[];
  readonly onChange: (updater: (analysisSettings: AnalysisSettings) => AnalysisSettings) => void;
}

const peakPlateMinDimensionsHelpText =
  'Minimum plate dimensions for peak plates apply when a positive slope is adjacent to a negative slope (i.e., at ' +
  'peak joints).';

function AnalysisSettingsControls(props: Props) {
  const { onChange, analysisSettingsArray } = props;

  const selectedModelFromState = useAppSelector(getSelectedModelForSettingsPanel);
  const selectedModel = // the `!== undefined` usage is intentional to allow overriding the selected model to null
    props.selectedModelOverride !== undefined ? props.selectedModelOverride : selectedModelFromState;
  const selectedModelIsTruss = selectedModel && selectedModel.type !== 'layout-model';
  const multiPlyConnectionTypes = uniq(
    analysisSettingsArray.map((analysisSettings) => analysisSettings.multiPlyConnectionType),
  );
  const showMultiPlyConnectionTypeWarning = selectedModelIsTruss
    ? multiPlyConnectionTypes.some(
        (multiPlyConnectionType) =>
          multiPlyConnectionType === 'Nailed' &&
          isTruss(selectedModel.model.component) &&
          selectedModel.model.component.numberOfPlies > 1 &&
          distanceGreaterThan(
            selectedModel.model.component.numberOfPlies * selectedModel.model.component.thickness,
            4.5,
          ),
      )
    : false;

  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const onChangeAllModels = useCallback(
    (changes: Partial<AnalysisSettings>) => {
      onChange((oldAnalysisSettings) => ({ ...oldAnalysisSettings, ...changes }));
    },
    [onChange],
  );

  const setChordBracing = useCallback(
    (
      chordBracingProperty: keyof ChordBracing,
      chordBracingSettingsProperty: keyof ChordBracingSettings,
      value: boolean | number,
    ) => {
      onChange((oldAnalysisSettings) => ({
        ...oldAnalysisSettings,
        chordBracing: {
          ...oldAnalysisSettings.chordBracing,
          [chordBracingProperty]: {
            ...oldAnalysisSettings.chordBracing[chordBracingProperty],
            [chordBracingSettingsProperty]: value,
          },
        },
      }));
    },
    [onChange],
  );

  const designStandardsArray = uniq(analysisSettingsArray.map((analysisSettings) => analysisSettings.designStandard));
  const designStandards = designStandardsArray
    .map((designStandard) => formatBuildingCodeOrDesignStandard(designStandard))
    .join(', ');
  const newestDesignStandard = maxBy(designStandardsArray, (designStandard) => getTpi1Year(designStandard) ?? 0);
  const temperatures = uniq(analysisSettingsArray.map((analysisSettings) => analysisSettings.temperature));

  const determinateSettings = getDeterminateValueOrNull(analysisSettingsArray);

  return (
    <PropertiesPanel
      title={props.showHeader !== false ? 'Analysis Settings' : undefined}
      subtitle={
        props.showHeader !== false && analysisSettingsArray.length > 0
          ? `Design Standard: ${designStandards}`
          : undefined
      }
    >
      {props.showPresetControls !== false && (
        <>
          <SettingsControlsPresetToolbar
            settings={determinateSettings}
            settingsType="analysisSettings"
            onOverwriteSettingsFromPreset={(settings) => onChangeAllModels(settings)}
          />
          <Divider />
        </>
      )}

      <Box sx={{ m: 1 }}>
        {analysisSettingsArray.length === 0 ? (
          <Typography sx={{ height: '100vh', textAlign: 'center' }}>
            Select object(s) on the left to view/edit settings.
          </Typography>
        ) : (
          <>
            <DeflectionLimitsSections
              isViewOnly={props.isViewOnly}
              expandedSection={expandedSection}
              setExpandedSection={setExpandedSection}
              analysisSettingsArray={analysisSettingsArray}
              onChange={onChange}
            />

            <AnalysisSettingsSection
              value="multi-ply"
              label="Multi Ply Settings"
              expandedSection={expandedSection}
              setExpandedSection={setExpandedSection}
            >
              <FormControl>
                <FormLabel>Connection Type:</FormLabel>
                <RadioGroup
                  aria-label="Connection Type:"
                  value={multiPlyConnectionTypes.length === 1 ? multiPlyConnectionTypes[0] : ''}
                  onChange={(_, newValue) => {
                    onChangeAllModels({ multiPlyConnectionType: newValue });
                  }}
                >
                  {multiPlyConnectionTypeValues.map((range) => (
                    <FormControlLabel
                      key={range.enum}
                      value={range.enum}
                      disabled={props.isViewOnly}
                      control={<Radio color="default" />}
                      label={range.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              {showMultiPlyConnectionTypeWarning ? (
                <InlineNotification type="warning">
                  The total thickness is greater than 4 1/2 inches and therefore likely too much for nails
                </InlineNotification>
              ) : null}
            </AnalysisSettingsSection>

            <AnalysisSettingsSection
              value="chord-bracing"
              label="Chord Bracing"
              expandedSection={expandedSection}
              setExpandedSection={setExpandedSection}
            >
              <ChordBracingSubPanel
                disabled={props.isViewOnly}
                chordBracingArray={analysisSettingsArray.map((analysisSettings) => analysisSettings.chordBracing)}
                setChordBracing={setChordBracing}
              />
            </AnalysisSettingsSection>

            <AnalysisSettingsSection
              value="plating"
              label="Plating Settings"
              expandedSection={expandedSection}
              setExpandedSection={setExpandedSection}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormControl aria-describedby="weight-helper-text">
                  <MultiValueNumberInput
                    label="JSI Limit"
                    values={analysisSettingsArray.map((analysisSettings) => analysisSettings.platingJsiLimit)}
                    min={0}
                    max={1}
                    submit={(platingJsiLimit) => onChangeAllModels({ platingJsiLimit })}
                    numberOfDecimals={2}
                    disabled={props.isViewOnly}
                  />
                </FormControl>

                <FormControl aria-describedby="weight-helper-text">
                  <MultiValueNumberInput
                    label={
                      <span>
                        Compressive Force Reduction C<sub>R</sub>
                      </span>
                    }
                    values={analysisSettingsArray.map(
                      (analysisSettings) => analysisSettings.plateCompressiveForceReduction,
                    )}
                    min={0}
                    max={1}
                    submit={(plateCompressiveForceReduction) => onChangeAllModels({ plateCompressiveForceReduction })}
                    disabled={props.isViewOnly}
                  />
                </FormControl>

                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <MultiValueNumberInput
                    label="Fabrication Tolerance for Front-Back Plates"
                    values={analysisSettingsArray.map(
                      (analysisSettings) => analysisSettings.fabricationTolerancePercentageFrontBackPlates,
                    )}
                    integers
                    min={0}
                    max={50}
                    submit={(fabricationTolerancePercentage) =>
                      onChangeAllModels({
                        fabricationTolerancePercentageFrontBackPlates: fabricationTolerancePercentage,
                      })
                    }
                    disabled={props.isViewOnly}
                    sx={{ width: '100%' }}
                    unit="%"
                  />
                  <TooltipIconButton title={<FabricationToleranceForFrontBackPlatesHelpText />}>
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <MultiValueNumberInput
                    label="Fabrication Tolerance for Top-Bottom Plates"
                    values={analysisSettingsArray.map(
                      (analysisSettings) => analysisSettings.fabricationTolerancePercentageTopBottomPlates,
                    )}
                    integers
                    min={0}
                    max={50}
                    submit={(fabricationTolerancePercentage) =>
                      onChangeAllModels({
                        fabricationTolerancePercentageTopBottomPlates: fabricationTolerancePercentage,
                      })
                    }
                    disabled={props.isViewOnly}
                    sx={{ width: '100%' }}
                    unit="%"
                  />
                  <TooltipIconButton title={<FabricationToleranceForTopBottomPlatesHelpText />}>
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <FormControl>
                  <MultiValueDistanceInput
                    label="Required Lateral Bite"
                    values={analysisSettingsArray.map((analysisSettings) => analysisSettings.requiredLateralBite)}
                    min={0}
                    // lateral bite should not be larger than half the plate width of the plates on the truss.
                    // (Otherwise it's difficult or impossible to satisfy the LateralBiteCheck)
                    // This value of 6" was picked to reflect that our largest plates are 12" wide.
                    max={6}
                    submit={(requiredLateralBite) => onChangeAllModels({ requiredLateralBite })}
                    disabled={props.isViewOnly}
                  />
                </FormControl>

                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <MultiValueBooleanInput
                    disabled={props.isViewOnly}
                    values={analysisSettingsArray.map(
                      (analysisSettings) => analysisSettings.maxPlateIntervalAlongStacks != null,
                    )}
                    onChange={(newValue) =>
                      void onChangeAllModels({
                        maxPlateIntervalAlongStacks: newValue ? defaultMaxPlateIntervalAlongStacks : null,
                      })
                    }
                    label="Add Plates Along Stacks"
                  />
                  <FormControl>
                    <MultiValueDistanceInput
                      label="Max Plate Interval"
                      submit={(interval) => void onChangeAllModels({ maxPlateIntervalAlongStacks: interval })}
                      values={analysisSettingsArray.map(
                        (analysisSettings) =>
                          analysisSettings.maxPlateIntervalAlongStacks ?? defaultMaxPlateIntervalAlongStacks,
                      )}
                      min={0}
                      disabled={
                        props.isViewOnly ||
                        analysisSettingsArray.every(
                          (analysisSettings) => analysisSettings.maxPlateIntervalAlongStacks == null,
                        )
                      }
                    />
                  </FormControl>
                  <TooltipIconButton title={platesAlongStacksHelpText}>
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <FormControl>
                  <MultiValueBooleanInput
                    label="Plate Ribbon Blocks"
                    values={analysisSettingsArray.map((analysisSettings) => analysisSettings.plateRibbonBlocks)}
                    onChange={(newValue) =>
                      void onChangeAllModels({
                        plateRibbonBlocks: newValue,
                      })
                    }
                    disabled={props.isViewOnly}
                  />
                </FormControl>

                <MultiValueDistanceInput
                  label="Heel Plate Min Width"
                  values={analysisSettingsArray.map((analysisSettings) => analysisSettings.heelPlateMinWidth)}
                  min={0}
                  submit={(heelPlateMinWidth) => onChangeAllModels({ heelPlateMinWidth })}
                  disabled={props.isViewOnly}
                />
                <MultiValueDistanceInput
                  label="Heel Plate Min Length"
                  values={analysisSettingsArray.map((analysisSettings) => analysisSettings.heelPlateMinLength)}
                  min={0}
                  submit={(heelPlateMinLength) => onChangeAllModels({ heelPlateMinLength })}
                  disabled={props.isViewOnly}
                />
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <MultiValueDistanceInput
                    sx={{ flex: 1 }}
                    label="Peak Plate Min Width"
                    values={analysisSettingsArray.map((analysisSettings) => analysisSettings.peakPlateMinWidth)}
                    min={0}
                    submit={(peakPlateMinWidth) => onChangeAllModels({ peakPlateMinWidth })}
                    disabled={props.isViewOnly}
                  />
                  <TooltipIconButton title={peakPlateMinDimensionsHelpText}>
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <MultiValueDistanceInput
                    sx={{ flex: 1 }}
                    label="Peak Plate Min Length"
                    values={analysisSettingsArray.map((analysisSettings) => analysisSettings.peakPlateMinLength)}
                    min={0}
                    submit={(peakPlateMinLength) => onChangeAllModels({ peakPlateMinLength })}
                    disabled={props.isViewOnly}
                  />
                  <TooltipIconButton title={peakPlateMinDimensionsHelpText}>
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <MultiValueBooleanInput
                  disabled={props.isViewOnly}
                  values={analysisSettingsArray.map((analysisSettings) => analysisSettings.tryOffCenterPlateShifts)}
                  onChange={(tryOffCenterPlateShifts) =>
                    void onChangeAllModels({
                      tryOffCenterPlateShifts,
                    })
                  }
                  label="Try Off-Center Plate Shifts"
                />
              </Box>
            </AnalysisSettingsSection>

            <AnalysisSettingsSection
              value="other"
              label="Other Settings"
              expandedSection={expandedSection}
              setExpandedSection={setExpandedSection}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MultiValueBooleanInput
                    disabled={props.isViewOnly}
                    values={analysisSettingsArray.map((analysisSettings) => analysisSettings.useWetServiceFactor)}
                    onChange={(newValue) => void onChangeAllModels({ useWetServiceFactor: newValue })}
                    label={
                      <span>
                        Use Wet Service Factor (C<sub>M</sub>)
                      </span>
                    }
                  />
                  <TooltipIconButton
                    title={`
                  Applicable when lumber moisture content will exceed 19% for an extended period of time or at time of fabrication.
See ${tpiSection('6.4.6')}.`}
                  >
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MultiValueBooleanInput
                    disabled={props.isViewOnly}
                    values={analysisSettingsArray.map(
                      (analysisSettings) => analysisSettings.useBendingModificationFactor,
                    )}
                    onChange={(newValue) => void onChangeAllModels({ useBendingModificationFactor: newValue })}
                    label={
                      <span>
                        Use Bending Modification Factor (K<sub>m</sub>)
                      </span>
                    }
                  />
                  <TooltipIconButton
                    title={
                      <span>
                        Applicable to bending capacity F<sub>b</sub> in check {tpiSection('7.3.5')} calculations. Will
                        only apply to sawn lumber. See {tpiSection('6.4.3')} for caveats.
                      </span>
                    }
                  >
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MultiValueBooleanInput
                    disabled={props.isViewOnly}
                    values={analysisSettingsArray.map(
                      (analysisSettings) => analysisSettings.useRepetitiveMemberIncrease,
                    )}
                    onChange={(newValue) => void onChangeAllModels({ useRepetitiveMemberIncrease: newValue })}
                    label={
                      <span>
                        Use Repetitive Member Increase (C<sub>r</sub>)
                      </span>
                    }
                  />
                  <TooltipIconButton
                    // prettier-ignore
                    title={
                  <span>
                    Increases design values for axial force and bending checks.
                    May be applicable when 3 or more trusses
                    are joined by sheathing/flooring and spaced no more than 2 ft. on center.
                    See {tpiSection('6.4.2')} for caveats.
                  </span>
                }
                  >
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <MultiValueBooleanInput
                  disabled={props.isViewOnly}
                  values={analysisSettingsArray.map(
                    (analysisSettings) => analysisSettings.assumeCompositeActionForStackedMembers,
                  )}
                  onChange={(newValue) => void onChangeAllModels({ assumeCompositeActionForStackedMembers: newValue })}
                  label={
                    <span>
                      {compositeActionSettingLabel}
                      <TooltipIconButton title={compositeActionHelpText}>
                        <HelpIcon />
                      </TooltipIconButton>
                    </span>
                  }
                />

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MultiValueBooleanInput
                    disabled={props.isViewOnly}
                    values={analysisSettingsArray.map(
                      (analysisSettings) =>
                        analysisSettings.pinEndVerticalJointsRoof ??
                        getDefaultValuePinEndVerticalJointsRoof(analysisSettings),
                    )}
                    onChange={(value: boolean) =>
                      onChange((oldAnalysisSettings) => ({
                        ...oldAnalysisSettings,
                        pinEndVerticalJointsRoof:
                          value === getDefaultValuePinEndVerticalJointsRoof(oldAnalysisSettings) ? null : value,
                      }))
                    }
                    label="Pin End Vertical Joints (Roof Trusses)"
                  />
                  <TooltipIconButton
                    title={
                      'Causes roof truss joints involving end verticals (or other similar joints) to be treated as ' +
                      'pinned rather than semi-rigidly or rigidly connected.' +
                      (newestDesignStandard == null || isTpi1VersionOrLater(newestDesignStandard, 2022)
                        ? ` See ${tpiSection('6.1.1.3.2(e)', newestDesignStandard ?? DesignStandard.Tpi2022)}.`
                        : '')
                    }
                  >
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <MultiValueBooleanInput
                    disabled={props.isViewOnly}
                    values={analysisSettingsArray.map((analysisSettings) => analysisSettings.useManualCamber)}
                    onChange={(newValue) => onChangeAllModels({ useManualCamber: newValue })}
                    label="Use Manual Camber"
                  />
                  <FormControl>
                    <MultiValueDistanceInput
                      label="Manual Camber"
                      submit={(manualCamber) => void onChangeAllModels({ manualCamber })}
                      values={analysisSettingsArray.map((analysisSettings) => analysisSettings.manualCamber)}
                      min={0}
                      disabled={
                        props.isViewOnly ||
                        analysisSettingsArray.every((analysisSettings) => !analysisSettings.useManualCamber)
                      }
                    />
                  </FormControl>
                  <TooltipIconButton title={'Overrides the camber on the Truss Design Drawing'}>
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControl aria-describedby="weight-helper-text">
                    <MultiValueNumberInput
                      label={
                        <span>
                          Creep Factor (K<sub>cr</sub>)
                        </span>
                      }
                      values={analysisSettingsArray.map((analysisSettings) => analysisSettings.creepFactor)}
                      submit={(creepFactor) => void onChangeAllModels({ creepFactor })}
                      disabled={props.isViewOnly}
                    />
                  </FormControl>
                  <TooltipIconButton
                    title={
                      'Factor applied to long-term deflections. Should be at least 2 for dry service conditions, and at least 3 for wet service conditions.'
                    }
                  >
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControl aria-describedby="weight-helper-text">
                    <MultiValueNumberInput
                      label={
                        <span>
                          Incising Factor (C<sub>i</sub>)
                        </span>
                      }
                      values={analysisSettingsArray.map((analysisSettings) => analysisSettings.incisedFactor)}
                      submit={(incisedFactor) => void onChangeAllModels({ incisedFactor })}
                      disabled={props.isViewOnly}
                    />
                  </FormControl>
                  <TooltipIconButton
                    title={`
Adjustment to account for lumber incising, used in some preservative-treated lumber. Applies to design values for some member strength checks.
See ${tpiSection('6.4.8')}.`}
                  >
                    <HelpIcon />
                  </TooltipIconButton>
                </Box>

                <FormControl margin="normal">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel>Temperature Range</FormLabel>
                    <TooltipIconButton
                      /* prettier-ignore */
                      title={
                    <span>
                      Harsher temperatures at the installation site require design value reductions.
                      See {tpiSection('6.4.7')} & Table 6.4-5.
                    </span>
                  }
                    >
                      <HelpIcon />
                    </TooltipIconButton>
                  </Box>
                  <RadioGroup
                    aria-label="Temperature Range"
                    value={temperatures.length === 1 ? temperatures[0] : ''}
                    onChange={(_, newValue) => onChangeAllModels({ temperature: newValue })}
                  >
                    {temperatureRanges.map((range) => (
                      <FormControlLabel
                        key={range.enum}
                        value={range.enum}
                        disabled={props.isViewOnly}
                        control={<Radio color="default" />}
                        label={range.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            </AnalysisSettingsSection>
          </>
        )}
      </Box>
    </PropertiesPanel>
  );
}

interface DeflectionLimitsSectionProps {
  readonly isViewOnly: boolean;
  readonly expandedSection: string | null;
  readonly setExpandedSection: (expandedKey: string | null) => void;
  readonly analysisSettingsArray: readonly AnalysisSettings[];
  readonly onChange: (updater: (analysisSettings: AnalysisSettings) => AnalysisSettings) => void;
}

function DeflectionLimitsSections(props: DeflectionLimitsSectionProps) {
  const componentType = useAppSelector(getComponentTypeForSettingsPanel);

  const deflectionLimitsControlsSharedProps = {
    isViewOnly: props.isViewOnly,
    deflectionSettingsArray: props.analysisSettingsArray.map((analysisSettings) => ({
      roofDeflectionSettings: analysisSettings.roofDeflectionLimits,
      floorDeflectionSettings: analysisSettings.floorDeflectionLimits,
    })),
    onRoofChange: (patch) => {
      props.onChange((oldAnalysisSettings) => ({
        ...oldAnalysisSettings,
        roofDeflectionLimits: {
          ...oldAnalysisSettings.roofDeflectionLimits,
          ...patch,
        },
      }));
    },
    onFloorChange: (patch) => {
      props.onChange((oldAnalysisSettings) => ({
        ...oldAnalysisSettings,
        floorDeflectionLimits: {
          ...oldAnalysisSettings.floorDeflectionLimits,
          ...patch,
        },
      }));
    },
  } as const satisfies Partial<DeflectionProps>;

  switch (componentType) {
    case null:
      return null;

    case 'Wall':
      console.error("Walls aren't supported.");
      return null;

    case 'FloorTruss':
    case 'RoofTruss': {
      return (
        <AnalysisSettingsSection
          value="deflection-limits-1"
          label="Deflection Limits"
          expandedSection={props.expandedSection}
          setExpandedSection={props.setExpandedSection}
        >
          <DeflectionLimitsControls
            {...deflectionLimitsControlsSharedProps}
            trussType={componentType}
            settingsForAllTrussTypesAreAccessible={false}
          />
        </AnalysisSettingsSection>
      );
    }

    case 'NonComponent':
      return (
        <>
          <AnalysisSettingsSection
            value="deflection-limits-1"
            label="Roof Deflection Limits"
            expandedSection={props.expandedSection}
            setExpandedSection={props.setExpandedSection}
          >
            <DeflectionLimitsControls
              {...deflectionLimitsControlsSharedProps}
              trussType="RoofTruss"
              settingsForAllTrussTypesAreAccessible
            />
          </AnalysisSettingsSection>

          <AnalysisSettingsSection
            value="deflection-limits-2"
            label="Floor Deflection Limits"
            expandedSection={props.expandedSection}
            setExpandedSection={props.setExpandedSection}
          >
            <DeflectionLimitsControls
              {...deflectionLimitsControlsSharedProps}
              trussType="FloorTruss"
              settingsForAllTrussTypesAreAccessible
            />
          </AnalysisSettingsSection>
        </>
      );

    default:
      assertExhaustiveSwitch(componentType);
  }
}

const temperatureRanges = [
  { label: 'T < 100°F', enum: 'LessThan100' },
  { label: '100°F ≤ T < 125°F', enum: 'GreaterThan100' },
  { label: '125°F ≤ T < 150°F', enum: 'GreaterThan125' },
];

const multiPlyConnectionTypeValues = [
  { label: 'Nailed', enum: 'Nailed' },
  { label: 'Screwed', enum: 'Screwed' },
  // https://trello.com/c/mcaZpBsJ/745-re-enable-bolted-multi-ply-connection
  // Currently disabled because we have no design values for bolts, and therefore analysis will not work if you select this
  // { label: 'Bolted', enum: 'Bolted' },
];

const textFieldSx = { m: 1, flex: 1 } as const;

interface DeflectionProps {
  readonly trussType: TrussType;
  readonly settingsForAllTrussTypesAreAccessible: boolean;
  readonly deflectionSettingsArray: readonly {
    readonly roofDeflectionSettings: RoofDeflectionSettings;
    readonly floorDeflectionSettings: FloorDeflectionSettings;
  }[];
  readonly isViewOnly: boolean;
  readonly onRoofChange: (changes: Partial<RoofDeflectionSettings>) => any;
  readonly onFloorChange: (changes: Partial<FloorDeflectionSettings>) => any;
}

function DeflectionLimitsControls(props: DeflectionProps) {
  const { trussType, deflectionSettingsArray, isViewOnly, onRoofChange, onFloorChange } = props;

  const roofDeflectionSettingsArray = deflectionSettingsArray.map(
    (deflectionSettings) => deflectionSettings.roofDeflectionSettings,
  );
  const floorDeflectionSettingsArray = deflectionSettingsArray.map(
    (deflectionSettings) => deflectionSettings.floorDeflectionSettings,
  );
  const deflectionSettingsArraysForTrussType =
    trussType === 'RoofTruss'
      ? roofDeflectionSettingsArray
      : trussType === 'FloorTruss'
        ? floorDeflectionSettingsArray
        : assertExhaustiveSwitch(trussType);
  const onChange =
    trussType === 'RoofTruss'
      ? onRoofChange
      : trussType === 'FloorTruss'
        ? onFloorChange
        : assertExhaustiveSwitch(trussType);

  type DeflectionSettings = typeof deflectionSettingsArraysForTrussType;

  const getDeflectionChangeCallback =
    <K extends keyof DeflectionSettings[number]>(key: K) =>
    (value: DeflectionSettings[number][K]) => {
      onChange({
        [key]: value,
      });
    };

  const loadColumnHeaders = (
    <Box sx={{ display: 'flex' }}>
      <Typography sx={textFieldSx} variant="caption">
        Span/Deflection
      </Typography>
      <Typography sx={textFieldSx} variant="caption">
        Absolute (inches)
      </Typography>
    </Box>
  );

  return (
    <Box component="span" sx={{ m: 1 }}>
      <Box sx={{ display: 'flex' }}>
        {loadCategories.map((loadCategory) => {
          const wallRatioLimitField = `${loadCategory}WallSpanToDeflectionRatio` as const;
          const wallAbsoluteLimitField = `${loadCategory}WallAbsoluteDeflection` as const;
          const horizontalLimitField = `${loadCategory}Horizontal` as const;

          // We don't currently check wall deflections, but it seems possible we'll implement that for TPI 1-2022.
          const areWallLimitsUsed = false;

          return (
            <Box sx={{ flex: 1, m: 1 }} key={loadCategory}>
              <Typography>{loadCategoryLabels[loadCategory]}</Typography>
              <Box sx={{ border: 'solid 1px', borderColor: 'border.main', p: 1, display: 'flex' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {loadColumnHeaders}
                  {(['Truss', 'TopChord', 'BottomChord', 'Cantilever', 'Overhang', 'Web'] as const).map((spanType) => {
                    const ratioLimitField = `${loadCategory}${spanType}SpanToDeflectionRatio` as const;
                    const absoluteLimitField = `${loadCategory}${spanType}AbsoluteDeflection` as const;

                    return (
                      <DeflectionLimitControls
                        label={deflectionElementLabels[spanType]}
                        ratioLimitProps={{
                          values: deflectionSettingsArraysForTrussType.map(
                            (deflectionSettings) => deflectionSettings[ratioLimitField],
                          ),
                          onChange: getDeflectionChangeCallback(ratioLimitField),
                          options: deflectionRatioLimitOptions[loadCategory][spanType],
                        }}
                        absoluteLimitProps={{
                          values: deflectionSettingsArraysForTrussType.map(
                            (deflectionSettings) => deflectionSettings[absoluteLimitField],
                          ),
                          onChange: getDeflectionChangeCallback(absoluteLimitField),
                        }}
                        isViewOnly={isViewOnly}
                        key={spanType}
                      />
                    );
                  })}
                  {areWallLimitsUsed && trussType === 'RoofTruss' ? (
                    <DeflectionLimitControls
                      label={deflectionElementLabels['Wall']}
                      ratioLimitProps={{
                        values: roofDeflectionSettingsArray.map(
                          (deflectionSettings) => deflectionSettings[wallRatioLimitField],
                        ),
                        onChange: (newValue) => onRoofChange({ [wallRatioLimitField]: newValue }),
                        options: deflectionRatioLimitOptions[loadCategory]['Wall'],
                      }}
                      absoluteLimitProps={{
                        values: roofDeflectionSettingsArray.map(
                          (deflectionSettings) => deflectionSettings[wallAbsoluteLimitField],
                        ),
                        onChange: (newValue) => onRoofChange({ [wallAbsoluteLimitField]: newValue }),
                      }}
                      isViewOnly={isViewOnly}
                    />
                  ) : null}
                  {trussType === 'RoofTruss' && loadCategory === 'live' ? (
                    <DeflectionLimitControls
                      label={deflectionElementLabels['AtticRoomFloor']}
                      ratioLimitProps={{
                        values: Object.values(deflectionSettingsArray).map(
                          (deflectionSettings) =>
                            deflectionSettings.roofDeflectionSettings.liveAtticRoomFloorSpanToDeflectionRatio ??
                            deflectionSettings.floorDeflectionSettings.liveTrussSpanToDeflectionRatio,
                        ),
                        onChange: (newValue) => {
                          for (const deflectionSettings of deflectionSettingsArray) {
                            deflectionSettings.roofDeflectionSettings.liveAtticRoomFloorSpanToDeflectionRatio == null
                              ? onFloorChange({ liveTrussSpanToDeflectionRatio: newValue })
                              : onRoofChange({ liveAtticRoomFloorSpanToDeflectionRatio: newValue });
                          }
                        },
                        options: deflectionRatioLimitOptions['live']['Truss'],
                      }}
                      absoluteLimitProps={{
                        values: Object.values(deflectionSettingsArray).map(
                          (deflectionSettings) =>
                            deflectionSettings.roofDeflectionSettings.liveAtticRoomFloorAbsoluteDeflection ??
                            deflectionSettings.floorDeflectionSettings.liveTrussAbsoluteDeflection,
                        ),
                        onChange: (newValue) => {
                          for (const deflectionSettings of deflectionSettingsArray) {
                            deflectionSettings.roofDeflectionSettings.liveAtticRoomFloorAbsoluteDeflection == null
                              ? onFloorChange({ liveTrussAbsoluteDeflection: newValue })
                              : onRoofChange({ liveAtticRoomFloorAbsoluteDeflection: newValue });
                          }
                        },
                      }}
                      toggleProps={
                        props.settingsForAllTrussTypesAreAccessible
                          ? {
                              label: 'Link to Floor Truss Limits',
                              values: roofDeflectionSettingsArray.map(
                                (deflectionSettings) =>
                                  deflectionSettings.liveAtticRoomFloorSpanToDeflectionRatio == null,
                              ),
                              onChange: (newValue) => {
                                for (const deflectionSettings of deflectionSettingsArray) {
                                  onRoofChange({
                                    liveAtticRoomFloorSpanToDeflectionRatio: newValue
                                      ? null
                                      : deflectionSettings.floorDeflectionSettings.liveTrussSpanToDeflectionRatio,
                                    liveAtticRoomFloorAbsoluteDeflection: newValue
                                      ? null
                                      : deflectionSettings.floorDeflectionSettings.liveTrussAbsoluteDeflection,
                                  });
                                }
                              },
                              hint:
                                'When this is on, changing the attic room floor deflection limits will also change ' +
                                'the overall deflection limits for floor trusses and vice versa.',
                            }
                          : null
                      }
                      isViewOnly={isViewOnly}
                    />
                  ) : null}
                  <DeflectionLimitControls
                    label={deflectionElementLabels['Horizontal']}
                    ratioLimitProps={null}
                    absoluteLimitProps={{
                      values: deflectionSettingsArraysForTrussType.map(
                        (deflectionSettings) => deflectionSettings[horizontalLimitField],
                      ),
                      onChange: getDeflectionChangeCallback(horizontalLimitField),
                    }}
                    isViewOnly={isViewOnly}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

interface UpdateableValuesProps<T> {
  readonly values: readonly T[];
  readonly onChange: (newValue: T) => void;
}

type DeflectionLimitControlsProps = {
  readonly label: string;
  readonly ratioLimitProps: null | (UpdateableValuesProps<number> & { readonly options: readonly number[] });
  readonly absoluteLimitProps: UpdateableValuesProps<number>;
  readonly toggleProps?:
    null | (UpdateableValuesProps<boolean> & { readonly label: string; readonly hint?: ReactNode });
  readonly isViewOnly: boolean;
};

function DeflectionLimitControls(props: DeflectionLimitControlsProps) {
  const { label, ratioLimitProps, absoluteLimitProps, toggleProps, isViewOnly } = props;

  const limitInputsSx = toggleProps ? ({ ...textFieldSx, marginBottom: 0 } as const) : textFieldSx;

  return (
    <Box>
      <Box sx={{ display: 'flex' }}>
        {ratioLimitProps ? (
          <FormControl sx={limitInputsSx}>
            <DeflectionLimitSelect
              label={label}
              deflectionLimitValues={ratioLimitProps.values}
              setDeflectionLimitValue={ratioLimitProps.onChange}
              deflectionLimitOptions={ratioLimitProps.options}
              disabled={isViewOnly}
            />
          </FormControl>
        ) : null}
        <MultiValueNumberInput
          label={label}
          values={absoluteLimitProps.values}
          submit={absoluteLimitProps.onChange}
          disabled={isViewOnly}
          sx={limitInputsSx}
        />
      </Box>
      {toggleProps ? (
        <Box sx={{ marginBottom: textFieldSx.m }}>
          <MultiValueBooleanInput
            values={toggleProps.values}
            disabled={isViewOnly}
            onChange={(checked) => toggleProps.onChange(checked)}
            label={toggleProps.label}
            sx={{ m: 0, marginLeft: textFieldSx.m }}
          />
          {toggleProps.hint ? (
            <TooltipIconButton title={toggleProps.hint}>
              <HelpIcon />
            </TooltipIconButton>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function AnalysisSettingsSection(props: {
  value: string;
  label: string;
  expandedSection: string | null;
  setExpandedSection: (expandedKey: string | null) => void;
  children?: ReactNode;
}) {
  return (
    <Accordion
      expanded={props.value === props.expandedSection}
      onChange={(_, isExpanded) => void props.setExpandedSection(isExpanded ? props.value : null)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{props.label}</Typography>
      </AccordionSummary>
      <AccordionDetails>{props.children}</AccordionDetails>
    </Accordion>
  );
}

const loadCategories = ['live', 'total'] as const;

const loadCategoryLabels = { live: 'Live Loads', total: 'Total Loads' } as const;

const deflectionElementLabels = {
  Truss: 'Truss',
  TopChord: 'TC Panel',
  BottomChord: 'BC Panel',
  Cantilever: 'Cantilever',
  Overhang: 'Overhang',
  Web: 'Web',
  Wall: 'Wall',
  AtticRoomFloor: 'Attic Room Floor',
  Horizontal: 'Horizontal',
} as const;

const optionValues = [80, 90, 120, 180, 240, 360, 480, 600, 720, 960];

const optionsStart80 = optionValues;

const optionsStart90 = optionsStart80.filter((option) => option >= 90);
const optionsStart120 = optionsStart80.filter((option) => option >= 120);
const optionsStart180 = optionsStart80.filter((option) => option >= 180);

const deflectionRatioLimitOptions = {
  live: {
    Truss: optionsStart180,
    TopChord: optionsStart180,
    BottomChord: optionsStart180,
    Cantilever: optionsStart90,
    Overhang: optionsStart90,
    Web: optionsStart180,
    Wall: optionsStart90,
  },
  total: {
    Truss: optionsStart120,
    TopChord: optionsStart120,
    BottomChord: optionsStart120,
    Cantilever: optionsStart80,
    Overhang: optionsStart80,
    Web: optionsStart120,
    Wall: optionsStart90,
  },
} as const;

// From TPI 1 Section 7.3.3.7
const defaultMaxPlateIntervalAlongStacks = 30;

const compositeActionSettingLabel = 'Assume Composite Action for Stacked Members';

const leftDoubleQuote = '\u201C';
const rightDoubleQuote = '\u201D';
const platesAlongStacksHelpText = `
  Adds plates where necessary along stacked members to achieve the specified maximum spacing.
  This can help to satisfy composite action modeling assumptions if the ${leftDoubleQuote}${compositeActionSettingLabel}${rightDoubleQuote} setting is on.
  See ${tpiSection('7.3.3.7')}.
`;

const compositeActionHelpText = `
  Treats stacked members as having greater bending stiffness than the sum of the stiffnesses of the layers.
  ${tpiSection('7.3.3.7')} specifies how to do this for two-layer stacks with sufficient plating.
  This setting applies the same specification to all stacked members.
`;

const FabricationToleranceForFrontBackPlatesHelpText = () => (
  <span>
    This determines the quality control factor for front-back plates. 1 minus this value is C<sub>q</sub>.
  </span>
);
const FabricationToleranceForTopBottomPlatesHelpText = () => (
  <span>
    This determines the quality control factor for top-bottom plates. 1 minus this value is C<sub>q</sub>.
  </span>
);

function getDefaultValuePinEndVerticalJointsRoof(analysisSettings: AnalysisSettings) {
  return isTpi1VersionOrLater(analysisSettings.designStandard, 2022);
}

/**
 * Wraps `AnalysisSettingsControls` for a single analysis settings object that integrates with the
 * `SettingsPanelContainer` system.
 */
export function SingleAnalysisSettingsControls({
  settingsObject,
  onChange,
  isViewOnly,
}: SettingsControlsProps<AnalysisSettings>) {
  const selectedModel = useAppSelector(getSelectedModelForSettingsPanel);
  if (!selectedModel) throw new Error('No model found for analysis settings controls');

  return (
    <AnalysisSettingsControls
      isViewOnly={isViewOnly}
      analysisSettingsArray={[settingsObject]}
      onChange={(updater) => {
        const updatedAnalysisSettings = updater(settingsObject);
        onChange(updatedAnalysisSettings);
      }}
    />
  );
}
